import { apiFetch, authProbe } from "./client";

export type Role = "root" | "super_admin" | "admin" | string;

export interface CurrentUser {
  id: number;
  email: string;
  full_name: string;
  role: Role;
}

// Cookie mode: the refresh token comes back as the partitioned HttpOnly cookie
// (__Host-blplus_rt) and refresh_delivery is "cookie". Some deployments (this
// UI's origin not in ADMIN_UI_ALLOWED_ORIGINS, or an API predating cookie mode)
// instead return the refresh token in the body; that still is a valid login and
// we keep it usable via the per-tab fallback store rather than refusing it.
// (docs/admin-ui-integration.md §1b)
interface LoginResponse {
  access_token: string;
  refresh_token?: string | null;
  refresh_delivery?: "cookie" | "body";
  // One-time lifeline for browsers that never store the cookie (Safari/ITP,
  // strict third-party-cookie settings). Absent in body mode.
  fallback_ticket?: string;
}

export async function login(email: string, password: string) {
  const payload = await apiFetch<LoginResponse>("/auth/login", {
    method: "POST",
    body: { email, password, refresh_delivery: "cookie" },
  });

  if (payload.refresh_delivery === "cookie") {
    // Modern cookie mode: nothing JS-readable is kept; the one-time ticket
    // still covers browsers that never store the cookie.
    stashFallbackTicket(payload.fallback_ticket);
    return payload;
  }

  // Body mode: cache the refresh token for this tab only (sessionStorage), the
  // same flow Safari/ITP browsers use, so /auth/refresh can rotate it. Cookie
  // mode is still preferred and used whenever the API grants it.
  if (typeof payload.refresh_token === "string" && payload.refresh_token) {
    cacheFallbackRefresh(payload.refresh_token);
  }
  stashFallbackTicket(payload.fallback_ticket);
  return payload;
}

// --- Safari / ITP fallback (docs/admin-ui-integration.md §1b) ---------------
//
// Browsers that never store the partitioned cookie can still keep a session:
// the login payload carries a one-time, minutes-lived `fallback_ticket`. When
// the cookie probe answers `no_refresh_cookie`, the UI spends the ticket once
// at POST /auth/refresh/fallback and caches the returned body refresh token
// for this tab (sessionStorage), rotating it on every /auth/refresh from then
// on — the pre-cookie behaviour, exclusively for browsers the cookie can
// never reach. On cookie-capable browsers nothing here ever activates: no
// ticket is spent, no body copy is ever cached.

// Carried in BOTH module memory and sessionStorage: memory covers same-tab
// flows and the vitest/node environment (where sessionStorage doesn't exist),
// sessionStorage is what survives a hard reload in the browser. It is per-tab
// by design, so the body copy never leaks across tabs.
const FALLBACK_TICKET_KEY = "blplus_fb_ticket";
const FALLBACK_REFRESH_KEY = "blplus_fb_refresh";
const fallbackTicketMemory: { current: string | null } = { current: null };
const fallbackRefreshMemory: { current: string | null } = { current: null };

function sessionGet(key: string): string | null {
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage.getItem(key);
  } catch {
    return null; // private mode / storage disabled: fallback just won't persist
  }
}

function sessionSet(key: string, value: string) {
  try {
    if (typeof sessionStorage !== "undefined") sessionStorage.setItem(key, value);
  } catch {
    // Same as above — the cookie path is unaffected.
  }
}

function sessionDel(key: string) {
  try {
    if (typeof sessionStorage !== "undefined") sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

function stashFallbackTicket(ticket: string | null | undefined) {
  fallbackTicketMemory.current = ticket ?? null;
  if (ticket) sessionSet(FALLBACK_TICKET_KEY, ticket);
  else sessionDel(FALLBACK_TICKET_KEY);
}

function takeFallbackTicket(): string | null {
  const ticket = sessionGet(FALLBACK_TICKET_KEY) ?? fallbackTicketMemory.current;
  fallbackTicketMemory.current = null;
  sessionDel(FALLBACK_TICKET_KEY);
  return ticket;
}

function cacheFallbackRefresh(token: string) {
  fallbackRefreshMemory.current = token;
  sessionSet(FALLBACK_REFRESH_KEY, token);
}

function peekFallbackRefresh(): string | null {
  return fallbackRefreshMemory.current ?? sessionGet(FALLBACK_REFRESH_KEY);
}

// Called on logout and whenever the cached copy turns out dead: the fallback
// credential must not outlive the session.
export function clearFallbackTokens() {
  fallbackTicketMemory.current = null;
  fallbackRefreshMemory.current = null;
  sessionDel(FALLBACK_TICKET_KEY);
  sessionDel(FALLBACK_REFRESH_KEY);
}

interface BodyTokenPair {
  access_token: string;
  refresh_token: string;
}

async function exchangeFallbackTicket(ticket: string): Promise<BodyTokenPair> {
  return apiFetch<BodyTokenPair>("/auth/refresh/fallback", {
    method: "POST",
    body: { ticket },
  });
}

async function rotateCachedRefresh(token: string): Promise<BodyTokenPair> {
  return apiFetch<BodyTokenPair>("/auth/refresh", {
    method: "POST",
    body: { refresh_token: token },
  });
}

export type RehydrateResult =
  | { kind: "authenticated"; accessToken: string; mode: "cookie" | "body" }
  | { kind: "no_session" }
  | { kind: "dead_session" };

// Re-establish the session on a cold mount (hard refresh, new tab). Order of
// attempts, first hit wins:
//
// 1. Cookie probe — modern browsers: the HttpOnly cookie comes back, the
//    token rotates inside it, a fresh access token returns in the body. The
//    refresh token is never readable.
// 2. Fallback-ticket exchange — Safari/ITP browsers: the probe proves the
//    cookie never arrived (`no_refresh_cookie`), so the login's one-time
//    ticket is spent for a body copy that this tab then rotates.
// 3. Cached body copy — Safari/ITP on every reload after the first: the
//    ticket is long gone (single-use), so the cached token rotates directly.
//
// Anything else is a dead session (`invalid_refresh_token`, spent/expired
// ticket, rejected cached copy) or no session at all — both end at the login
// screen, but the distinction keeps logs and tests honest.
export async function rehydrateSession(): Promise<RehydrateResult> {
  const { status, json } = await authProbe<{ access_token?: string }>("/auth/refresh");

  if (status === 200 && json?.payload?.access_token) {
    return { kind: "authenticated", accessToken: json.payload.access_token, mode: "cookie" };
  }

  const first = json?.errors?.[0];
  const code =
    first && typeof first === "object" && "code" in first ? String(first.code) : null;

  if (code !== "no_refresh_cookie") return { kind: "dead_session" };

  // The cookie never arrived. Old-browser fallback, in order: the login's
  // one-time ticket (first reload after login), then the cached body copy
  // (every reload after that).
  const ticket = takeFallbackTicket();
  if (ticket) {
    try {
      const tokens = await exchangeFallbackTicket(ticket);
      cacheFallbackRefresh(tokens.refresh_token);
      return { kind: "authenticated", accessToken: tokens.access_token, mode: "body" };
    } catch {
      clearFallbackTokens();
      return { kind: "dead_session" };
    }
  }

  const cached = peekFallbackRefresh();
  if (cached) {
    try {
      const tokens = await rotateCachedRefresh(cached);
      cacheFallbackRefresh(tokens.refresh_token);
      return { kind: "authenticated", accessToken: tokens.access_token, mode: "body" };
    } catch {
      // Dead copy: drop it so we stop retrying a doomed rotation.
      clearFallbackTokens();
      return { kind: "dead_session" };
    }
  }

  return { kind: "no_session" };
}

// /auth/me wraps the user as { user: {...} } (app/api/v1/auth.py).
export async function getCurrentUser() {
  const { user } = await apiFetch<{ user: CurrentUser }>("/auth/me");
  return user;
}
